import React from 'react';
import { injectIntl, InjectedIntl, FormattedHTMLMessage } from 'react-intl';

import {
    VerificationService,
    ErrorId,
    ErrorResponse,
    getSafe,
    Segment,
} from '@sheerid/jslib';
import { getRedirectUrl } from '../../lib/utils/stepComponentHelpers';
import { VerificationResult } from '../../lib/types/runtimeTypes';
import { redirect } from '../../lib/utils/browser';


interface StepErrorProps {
    verificationService: VerificationService;
    errorId?: ErrorId;
    intl?: InjectedIntl;
}

const StepError = ({ verificationService, errorId, intl }: StepErrorProps) => {
    const verificationResponse = verificationService.verificationResponse as ErrorResponse;
    const huluRedirectUrl = getRedirectUrl(verificationService, VerificationResult.FAILURE);

    const recoverableErrorIds: ErrorId[] = [
        'apiRateLimitExceeded',
        'failedToFetch',
        'internalServerError',
        'expiredVerification',
        'requestTimeout',
    ];
    const unrecoverableErrorIds: ErrorId[] = [
        'expiredEmailLoopToken',
        'expiredProgram',
        'invalidEmailLoopToken',
        'noRemainingRewardCodes',
        'invalidProgram',
        'noProgram',
        'simulatedError',
        'unauthorizedDomain',
    ];
    const ineligibleErrorIds: ErrorId[] = [
        'fraudRulesReject',
        'verificationLimitExceeded',
        'docReviewLimitExceeded',
    ];

    const fetchNewVerificationRequest = React.useCallback(() => {
        const currentSegment = getSafe<Segment>(() => verificationService.verificationResponse.segment);
        const trackingId: string = undefined;

        verificationService.fetchNewVerificationRequest(
            verificationService.programId,
            currentSegment,
            verificationService.previousViewModel,
            trackingId,
        );
    }, [verificationService.programId, verificationService.previousViewModel]);


    React.useEffect(() => {
        // @ts-expect-error - gtag is defined on the window object in index.html
        window.gtag('event', 'page_view', {
            page_title: intl.formatMessage({
                id: `errorTitle.${verificationResponse.errorIds[0]}`,
                defaultMessage: `${verificationResponse.errorIds[0]}`,
            }),
            page_location: `${window.location.pathname}${window.location.search}`,
            page_path: 'error',
        });
    }, []);

    return (
        <div id="sid-step-error" className="sid-l-container">
            <div className="sid-header sid-l-horz-center">
                <div className="sid-header__title sid-l-space-top-md">
                    <FormattedHTMLMessage
                        id={`errorTitle.${verificationResponse.errorIds[0]}`}
                        defaultMessage="Something's not quite right. We will look into it."
                    />
                </div>
                <div className="sid-header__subtitle sid-l-space-top-md">
                    <div className="sid-error-msg">
                        {
                            verificationResponse.errorIds ?
                            (
                                <p>
                                    {
                                        verificationResponse.errorIds.map(
                                            errId => intl.formatMessage({ id: `errorId.${errId}`, defaultMessage: `${errId}` }),
                                        ).join(', ')
                                    }
                                </p>
                            )
                            : null
                        }
                    </div>
                </div>

                <div className="sid-submit sid-l-space-top-md">
                    {
                        unrecoverableErrorIds.includes(verificationResponse.errorIds[0]) ?
                            <button
                                onClick={() => redirect(huluRedirectUrl)}
                                className="sid-btn sid-btn--dark"
                            >
                                <FormattedHTMLMessage id="gotIt" defaultMessage="Use code now" />
                            </button>
                            : null
                    }
                    {
                        recoverableErrorIds.includes(verificationResponse.errorIds[0]) ?
                            <button
                                onClick={fetchNewVerificationRequest}
                                onKeyPress={fetchNewVerificationRequest}
                                className="sid-btn sid-btn--dark"
                            >
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Use code now" />
                            </button>
                            : null
                    }
                    {
                        ineligibleErrorIds.includes(verificationResponse.errorIds[0]) ?
                            <button
                                onClick={() => redirect(huluRedirectUrl)}
                                className="sid-btn sid-btn--dark"
                            >
                                <FormattedHTMLMessage id="viewOtherHuluPlans" defaultMessage="Use code now" />
                            </button>
                            : null
                    }
                </div>
            </div>
        </div>
    );
};

export const StepErrorComponent = injectIntl(StepError);
